import { useEffect, useState } from "react";
import {
    FormControl,
    FormLabel,
    Input,
    Button,
    HStack,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Box,
    Text,
    VStack,
    Checkbox,
    useDisclosure,
    Divider,
    Badge,
    Textarea,
    Alert,
    AlertIcon,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionIcon,
    AccordionPanel
} from "@chakra-ui/react";

const riskLevels = ["High", "Moderate", "Low", "Straightforward"];

const riskOptions = {
    "Straightforward": {
        type: "single",
        options: ["Minimal risk of morbidity from additional diagnostic testing or treatment"]
    },
    "Low": {
        type: "single",
        options: ["Low risk of morbidity from additional diagnostic testing or treatment"]
    },
    "Moderate": {
        type: "grouped",
        mainOption: "Moderate risk of morbidity from additional diagnostic testing or treatment",
        suboptions: [
            "Prescription drug management",
            "Decision regarding minor surgery with identified patient or procedure risk factors",
            "Decision regarding elective major surgery without identified patient or procedure risk factors",
            "Diagnosis or treatment significantly limited by social determinants of health"
        ]
    },
    "High": {
        type: "grouped",
        mainOption: "High risk of morbidity from additional diagnostic testing or treatment",
        suboptions: [
            "Drug therapy requiring intensive monitoring for toxicity",
            "Decision regarding elective major surgery with identified patient or procedure risk factors",
            "Decision regarding emergency major surgery",
            "Decision regarding hospitalization",
            "Decision not to resuscitate or to de-escalate care because of poor prognosis"
        ]
    }
};

export default function RiskComplicationsSelector({ value, onChange }) {
    const [selectedOptions, setSelectedOptions] = useState({});
    const [selectedSuboptions, setSelectedSuboptions] = useState({});
    const [inputValue, setInputValue] = useState(value || "");
    const [previewText, setPreviewText] = useState("");

    const { isOpen, onOpen, onClose } = useDisclosure();

    useEffect(() => {
        setInputValue(value || "");
    }, [value]);

    // Update preview when selections change (only if modal is open)
    useEffect(() => {
        if (isOpen && hasSelections()) {
            const structuredText = generateStructuredText();
            if (structuredText) {
                setPreviewText(structuredText);
            }
        }
    }, [selectedOptions, selectedSuboptions, isOpen]);

    // When modal opens, set preview to current input value
    useEffect(() => {
        if (isOpen) {
            setPreviewText(inputValue);
        }
    }, [isOpen, inputValue]);

    const handleSingleOptionChange = (level, item) => {
        setSelectedOptions((prev) => {
            const current = prev[level] || [];
            const updated = current.includes(item)
                ? []
                : [item];
            return { ...prev, [level]: updated };
        });
    };

    const handleMainOptionChange = (level) => {
        const config = riskOptions[level];
        const mainOption = config.mainOption;

        setSelectedOptions((prev) => {
            const current = prev[level] || [];
            const isCurrentlySelected = current.includes(mainOption);
            const updated = isCurrentlySelected ? [] : [mainOption];

            // If unchecking main option, also clear all suboptions
            if (isCurrentlySelected) {
                setSelectedSuboptions((prevSub) => ({
                    ...prevSub,
                    [level]: []
                }));
            }

            return { ...prev, [level]: updated };
        });
    };

    const handleSuboptionChange = (level, suboption) => {
        const config = riskOptions[level];
        const mainOption = config.mainOption;

        setSelectedSuboptions((prev) => {
            const current = prev[level] || [];
            const updated = current.includes(suboption)
                ? current.filter((opt) => opt !== suboption)
                : [...current, suboption];

            // If selecting any suboption, automatically select the main option
            if (!current.includes(suboption)) {
                setSelectedOptions((prevMain) => {
                    const currentMain = prevMain[level] || [];
                    if (!currentMain.includes(mainOption)) {
                        return { ...prevMain, [level]: [mainOption] };
                    }
                    return prevMain;
                });
            }

            return { ...prev, [level]: updated };
        });
    };

    // Helper function to generate structured text from selections
    const generateStructuredText = () => {
        const selectedText = [];

        riskLevels.forEach(level => {
            const config = riskOptions[level];

            if (config.type === "single") {
                const levelOptions = selectedOptions[level] || [];
                if (levelOptions.length > 0) {
                    selectedText.push(levelOptions[0]);
                }
            } else if (config.type === "grouped") {
                const mainSelected = selectedOptions[level] || [];
                const subSelected = selectedSuboptions[level] || [];

                if (mainSelected.length > 0) {
                    let levelText = mainSelected[0];
                    if (subSelected.length > 0) {
                        levelText += ` [${subSelected.join("; ")}]`;
                    }
                    selectedText.push(levelText);
                }
            }
        });

        return selectedText.join(", ");
    };

    const handleApply = () => {
        // Apply exactly what's in the preview
        const finalValue = previewText.trim();
        setInputValue(finalValue);
        onChange?.(finalValue);
        onClose();
    };

    const handleClear = () => {
        setSelectedOptions({});
        setSelectedSuboptions({});
        setPreviewText("");
    };

    const handleModalClose = () => {
        // Clear selections when closing/canceling to reset state for next time
        setSelectedOptions({});
        setSelectedSuboptions({});
        onClose();
    };

    const hasSelections = () => {
        return Object.values(selectedOptions).some(level => level && level.length > 0) ||
            Object.values(selectedSuboptions).some(level => level && level.length > 0);
    };

    const renderSingleLevel = (level) => {
        const config = riskOptions[level];
        const selectedItems = selectedOptions[level] || [];

        return (
            <Box key={level} mb={6}>
                <HStack justify="space-between" mb={3}>
                    <Text fontWeight="bold" fontSize="lg" color="blue.600">
                        {level}
                    </Text>
                    {selectedItems.length > 0 && (
                        <Badge colorScheme="blue" variant="subtle">
                            {selectedItems.length} selected
                        </Badge>
                    )}
                </HStack>
                <VStack align="start" spacing={2} pl={2}>
                    {config.options.map((item) => (
                        <Checkbox
                            key={item}
                            isChecked={selectedItems.includes(item)}
                            onChange={() => handleSingleOptionChange(level, item)}
                            colorScheme="green"
                        >
                            <Text fontSize="sm">{item}</Text>
                        </Checkbox>
                    ))}
                </VStack>
            </Box>
        );
    };

    const renderGroupedLevel = (level) => {
        const config = riskOptions[level];
        const mainSelected = selectedOptions[level] || [];
        const subSelected = selectedSuboptions[level] || [];
        const isMainSelected = mainSelected.includes(config.mainOption);

        return (
            <Box key={level} mb={6}>
                <Text fontWeight="bold" fontSize="lg" mb={3} color="blue.600">
                    {level}
                </Text>
                <VStack align="start" spacing={3} pl={2}>
                    {/* Main option */}
                    <Box w="100%" border="1px" borderColor="gray.200" borderRadius="md" p={3}>
                        <HStack justify="space-between" mb={2}>
                            <Checkbox
                                isChecked={isMainSelected}
                                onChange={() => handleMainOptionChange(level)}
                                fontWeight="semibold"
                                colorScheme="green"
                            >
                                <Text fontSize="sm">{config.mainOption}</Text>
                            </Checkbox>
                            {subSelected.length > 0 && (
                                <Badge colorScheme="blue" variant="subtle">
                                    {subSelected.length} selected
                                </Badge>
                            )}
                        </HStack>

                        {/* Suboptions */}
                        <Box pl={6}>
                            <VStack align="start" spacing={2}>
                                {config.suboptions.map((suboption) => (
                                    <Checkbox
                                        key={suboption}
                                        size="sm"
                                        isChecked={subSelected.includes(suboption)}
                                        onChange={() => handleSuboptionChange(level, suboption)}
                                        colorScheme="blue"
                                    >
                                        <Text fontSize="sm">{suboption}</Text>
                                    </Checkbox>
                                ))}
                            </VStack>
                        </Box>
                    </Box>
                </VStack>
            </Box>
        );
    };

    return (
        <FormControl>
            <FormLabel>Risk of complications and Pt Mgmt.</FormLabel>
            <HStack spacing={2} align="center">
                <Input
                    placeholder="Type or select options..."
                    value={inputValue}
                    onChange={(e) => {
                        setInputValue(e.target.value);
                        onChange?.(e.target.value);
                    }}
                />
                <Button size="sm" variant="outline" onClick={onOpen}>
                    Select
                </Button>
            </HStack>

            <Modal isOpen={isOpen} onClose={handleModalClose} size="xl" scrollBehavior="inside">
                <ModalOverlay bg="blackAlpha.300" />
                <ModalContent maxH="85%" maxW="75%" w="auto" minW="40%" overflowY="auto" overflowX="hidden" borderRadius="md">
                    <ModalHeader>Select Risk Complexity Options</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {riskLevels.map((level, index) => {
                            const config = riskOptions[level];

                            return (
                                <Box key={level}>
                                    {config.type === "single"
                                        ? renderSingleLevel(level)
                                        : renderGroupedLevel(level)
                                    }
                                    {index !== riskLevels.length - 1 && <Divider my={4} />}
                                </Box>
                            );
                        })}

                        <Divider my={4} />
                        <Accordion allowToggle mb={3}>
                            <AccordionItem border="none">
                                <h2>
                                    <AccordionButton px={0} py={1} _hover={{ bg: "transparent" }} display="inline-flex" alignItems="center">
                                        <Text fontSize="sm" fontWeight="semibold" color="gray.700" mr={2}>
                                            Instructions
                                        </Text>
                                        <AccordionIcon />
                                    </AccordionButton>
                                </h2>
                                <AccordionPanel px={0} pt={2} pb={1} fontSize="sm" color="gray.600" lineHeight="1.7">
                                    • Use the options above to generate structured text automatically.<br />
                                    • The result appears below in the <b>Preview</b> section and can be customized manually.<br />
                                    • <Text as="span" fontWeight="semibold" color="red.400">Important:</Text> Make manual changes only <i>after</i> selecting options — new selections will overwrite existing text.
                                </AccordionPanel>
                            </AccordionItem>
                        </Accordion>

                        {/* Editable Live Preview Box */}
                        <Box>
                            <HStack justify="space-between" mb={3}>
                                <Text fontWeight="bold" fontSize="lg" color="green.600">
                                    Preview
                                </Text>
                                <HStack spacing={2}>
                                    <Badge colorScheme="gray" variant="subtle" fontSize="xs">
                                        Editable
                                    </Badge>
                                    {hasSelections() && (
                                        <Button
                                            size="xs"
                                            variant="ghost"
                                            colorScheme="blue"
                                            onClick={() => setPreviewText(generateStructuredText())}
                                        >
                                            Reset to Selected
                                        </Button>
                                    )}
                                </HStack>
                            </HStack>

                            <Box
                                border="1px"
                                borderColor="gray.200"
                                borderRadius="md"
                                bg="green.50"
                                position="relative"
                                _focusWithin={{
                                    borderColor: "gray.400",
                                    bg: "green.25"
                                }}
                            >
                                <Textarea
                                    value={previewText}
                                    onChange={(e) => setPreviewText(e.target.value)}
                                    placeholder="Select options above or type directly here.."
                                    minH="100px"
                                    maxH="200px"
                                    resize="vertical"
                                    border="none"
                                    bg="transparent"
                                    fontSize="sm"
                                    lineHeight="1.6"
                                    color="gray.700"
                                    _focus={{
                                        boxShadow: "none",
                                        outline: "none"
                                    }}
                                    _placeholder={{
                                        color: "gray.400",
                                        fontStyle: "italic"
                                    }}
                                />
                            </Box>

                            <HStack justify="space-between" mt={2}>
                                <Text fontSize="xs" color="gray.500">
                                    Edit directly or use options above • This content will be saved exactly as shown
                                </Text>
                                <Text fontSize="xs" color="blue.500" fontWeight="medium">
                                    {previewText ? `${previewText.length} characters` : ""}
                                </Text>
                            </HStack>
                        </Box>
                    </ModalBody>
                    <ModalFooter justifyContent="space-between">
                        <Button variant="ghost" colorScheme="red" onClick={handleClear}>
                            Clear
                        </Button>
                        <HStack spacing={2}>
                            <Button variant="ghost" onClick={handleModalClose}>
                                Cancel
                            </Button>
                            <Button colorScheme="blue" onClick={handleApply}>
                                Apply
                            </Button>
                        </HStack>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </FormControl>
    );
}